import React from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AlertOctagon } from "lucide-react";
import { Button } from "components/ui/button";

interface IErrorPageProps {
  title: string;
  message: string;
  backLink?: string;
  onRetry?: () => void;
  error?: Error | string;
}

export const ErrorPage = ({
  title,
  message,
  backLink = "/",
  error,
  onRetry,
}: IErrorPageProps) => {
  const { t } = useTranslation(["error"]);

  return (
    <div className="flex flex-col space-y-10 h-[75vh] flex-1 items-center justify-center text-center max-w-xl mx-auto container">
      <div className="rounded-full bg-icon-error-ring p-3">
        <div className="rounded-full bg-icon-error-background p-2">
          <AlertOctagon
            aria-hidden="true"
            className="text-system-error h-8 w-8"
          />
        </div>
      </div>

      <div className="space-y-2">
        <h1 className="font-semibold text-2xl">{title}</h1>
        <p className="text-tertiary-400 font-light whitespace-pre">{message}</p>
      </div>

      {error && (
        <p className="text-destructive">
          {error instanceof Error ? error.message : error}
        </p>
      )}

      <div className="grid grid-flow-col gap-3">
        <Link
          to="/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="support-action"
          className="inline-flex items-center justify-center rounded-xl border border-tertiary-300 bg-white px-4 py-2 text-sm font-medium text-tertiary-700 hover:bg-tertiary-50"
        >
          {t("error:actions.support")}
        </Link>

        {onRetry ? (
          <Button
            variant="primary"
            size="sm"
            onClick={onRetry}
            className="text-sm"
          >
            {t("error:actions.reload")}
          </Button>
        ) : (
          <Link
            to={backLink}
            className="inline-flex items-center justify-center rounded-xl bg-primary-500 px-4 py-2 text-sm font-normal text-white hover:bg-primary-600"
          >
            {t("error:actions.back")}
          </Link>
        )}
      </div>
    </div>
  );
};
